// Career progression: Trainee Counselor -> Advocate -> Counselor.
// Rank and per-case best scores persist in localStorage.
// NOTE ON STORAGE: this app is intentionally login-free. Everything stored here
// (display name, per-case best scores, knowledge check flags) is non-sensitive
// training preference data. No survivor data, credentials, or PII beyond a
// self-chosen display name ever touches browser storage.

const devWarn = (...args) => {
  if (process.env.NODE_ENV === "development") console.warn(...args);
};

const STORAGE_KEY = "safepath_progress_v1";
const NAME_KEY = "sps_trainee_name";
const KNOWLEDGE_KEY = "sps_knowledge_v1";
const COHORT_KEY = "sps_cohort";

export function loadCohort() {
  try {
    return localStorage.getItem(COHORT_KEY) || "";
  } catch {
    return "";
  }
}

export function saveCohort(code) {
  try {
    localStorage.setItem(COHORT_KEY, code.trim());
  } catch (err) {
    devWarn("Could not persist cohort code:", err);
  }
}

export function loadKnowledge() {
  try {
    return JSON.parse(localStorage.getItem(KNOWLEDGE_KEY)) || {};
  } catch {
    return {};
  }
}

export function saveKnowledge(phase, result) {
  const k = loadKnowledge();
  k[phase] = { score: result.score, total: result.total, id: result.id };
  try {
    localStorage.setItem(KNOWLEDGE_KEY, JSON.stringify(k));
  } catch (err) {
    devWarn("Could not persist knowledge check result:", err);
  }
  return k;
}

export function loadTraineeName() {
  try {
    return localStorage.getItem(NAME_KEY) || "";
  } catch {
    return "";
  }
}

export function saveTraineeName(name) {
  try {
    localStorage.setItem(NAME_KEY, name.trim());
  } catch (err) {
    devWarn("Could not persist trainee name:", err);
  }
}

export function resetProgress() {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(KNOWLEDGE_KEY);
  } catch (err) {
    devWarn("Could not clear saved progress:", err);
  }
  return { rank: "trainee", cases: {} };
}

export const RANKS = {
  trainee: {
    level: 1,
    title: "Trainee Counselor",
    meaning:
      "You are learning the core of survivor-centered advocacy: listen first, protect safety, and follow the survivor's lead.",
  },
  advocate: {
    level: 2,
    title: "Advocate",
    meaning:
      "You have shown you can keep a survivor's safety and autonomy at the center of a real case.",
  },
  counselor: {
    level: 3,
    title: "Counselor",
    meaning:
      "Across every modality you consistently protected safety, built trust, and planned collaboratively.",
  },
};

export const RANK_ORDER = ["trainee", "advocate", "counselor"];

export const COMPETENCIES = [
  { key: "safety", label: "Safety", note: "You kept immediate danger front and center." },
  { key: "trustworthiness", label: "Trustworthiness", note: "You were honest about limits and followed through." },
  { key: "choice", label: "Choice", note: "You protected the survivor's right to decide." },
  { key: "collaboration", label: "Collaboration", note: "You planned with the survivor, not for them." },
  { key: "empowerment", label: "Empowerment", note: "You built on the survivor's strengths and options." },
];

export const ADVOCATE_THRESHOLD = 70;
export const COUNSELOR_AVG = 80;
export const COUNSELOR_MIN_CASES = 4;

const DEFAULT_PROGRESS = { rank: "trainee", cases: {} };

export function loadProgress() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_PROGRESS };
    const parsed = JSON.parse(raw);
    return { rank: parsed.rank || "trainee", cases: parsed.cases || {} };
  } catch {
    return { ...DEFAULT_PROGRESS };
  }
}

function saveProgress(progress) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
  } catch (err) {
    devWarn("Could not persist progress; keeping in-memory only:", err);
  }
}

// Case score 0-100: half from choice quality, half from final meter health.
export function computeCaseScore(finalMeters, choices) {
  const total = choices.length || 1;
  const points = choices.reduce((sum, c) => {
    if (c.tone === "good") return sum + 100;
    if (c.tone === "caution") return sum + 55;
    return sum + 10; // harmful
  }, 0);
  const choiceAvg = points / total;
  const meterAvg =
    finalMeters.safety * 0.35 +
    finalMeters.trust * 0.3 +
    finalMeters.resources * 0.2 +
    finalMeters.time * 0.15;
  return Math.round(0.5 * choiceAvg + 0.5 * meterAvg);
}

export function earnedRank(cases) {
  const scores = Object.values(cases);
  const avg = scores.length ? scores.reduce((a, b) => a + b, 0) / scores.length : 0;
  if (scores.length >= COUNSELOR_MIN_CASES && avg >= COUNSELOR_AVG) return "counselor";
  if (scores.some((s) => s >= ADVOCATE_THRESHOLD)) return "advocate";
  return "trainee";
}

// Records a completed case, updates rank (never demotes), returns promotion info.
export function recordCase(caseId, score, endedEarly) {
  const p = loadProgress();
  const oldRank = p.rank;
  if (!endedEarly) {
    const prevBest = p.cases[caseId] || 0;
    p.cases[caseId] = Math.max(prevBest, score);
    const earned = earnedRank(p.cases);
    if (RANKS[earned].level > RANKS[p.rank].level) p.rank = earned;
  }
  saveProgress(p);
  return { progress: p, oldRank, newRank: p.rank, promoted: p.rank !== oldRank };
}

export const indefinite = (word = "") => (/^[aeiou]/i.test(word) ? "an" : "a");

export function nextGoal(rank) {
  if (rank === "trainee")
    return "Score 70% or higher on any case to become an Advocate.";
  if (rank === "advocate")
    return "Complete four cases with an 80% average or higher to become a Counselor.";
  return "You have reached the top rank. Keep sharpening your practice.";
}
